import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContactField from './ContactField';
import { ButtonRounded } from '../Buttons/ButtonRounded';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../../styles/UISystem';
import { ALL_CONTACT_TYPES } from '../../../utils/contactTypeConfig';

/**
 * AddContactOverlay
 *
 * Centered modal overlay for adding a new contact field. Opens after user selects
 * a contact type from the picker. Contains a single ContactField and a Save button.
 *
 * Behavior:
 * - Auto-focuses the input on open
 * - Save: if value exists, calls onSave and closes; if empty, just closes
 * - Tap outside: closes (discards)
 *
 * @param {boolean} isOpen - Whether overlay is visible
 * @param {string} type - Contact type (phone, email, instagram, etc.)
 * @param {string} fieldKey - Storage key (phone, phone2, email, etc.)
 * @param {function} onSave - Called with (fieldKey, value) when saved
 * @param {function} onClose - Called when overlay should dismiss
 */
const AddContactOverlay = ({ isOpen, type, fieldKey, onSave, onClose }) => {
  const [value, setValue] = useState('');

  // Reset value when overlay opens with new type
  useEffect(() => {
    if (isOpen) {
      setValue('');
    }
  }, [isOpen, type]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      };
    }
  }, [isOpen]);

  // Handle backdrop click - dismiss/discard
  const handleBackdropClick = () => {
    onClose();
  };

  // Handle save
  const handleSave = () => {
    if (value.trim()) {
      onSave(fieldKey, value.trim());
    }
    onClose();
  };

  // Get display label for the type
  const baseType = type?.replace(/\d+$/, '');
  const label = ALL_CONTACT_TYPES[baseType]?.label || 'Contact';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          {/* Centered modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '340px',
              backgroundColor: 'white',
              borderRadius: BORDER_RADIUS.PILL,
              padding: '24px 20px 20px',
              boxShadow: SHADOWS.TIER3_UP,
            }}
          >
            {/* Title */}
            <h2
              style={{
                fontSize: '18px',
                fontWeight: '600',
                color: COLORS.DARK_GRAY,
                textAlign: 'center',
                marginBottom: '20px',
              }}
            >
              Add {label}
            </h2>

            {/* Contact field */}
            <ContactField
              type={type}
              value={value}
              fieldKey={fieldKey}
              disabled={false}
              showLinks={false}
              onChange={setValue}
              onClear={() => setValue('')}
              autoFocus={true}
            />

            {/* Save button */}
            <div style={{ marginTop: '8px' }}>
              <ButtonRounded text="Save" onClick={handleSave} size="full" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddContactOverlay;
